/** Exact money contract: decimal parsing never rounds, and JSON never carries bigint. */
export const CURRENCY_MINOR_DIGITS = Object.freeze({ AZN: 2, USD: 2, EUR: 2, JPY: 0, RUB: 2, TRY: 2, KRW: 0 });
export function minorDigitsForCurrency(currency) {
    if (!Object.prototype.hasOwnProperty.call(CURRENCY_MINOR_DIGITS, currency))
        throw new Error('UNSUPPORTED_CURRENCY');
    return CURRENCY_MINOR_DIGITS[currency];
}
export function canonicalMinor(value) {
    if (typeof value !== 'string' && typeof value !== 'bigint')
        throw new Error('INVALID_MINOR_AMOUNT');
    const text = String(value);
    if (!/^-?(?:0|[1-9]\d{0,59})$/.test(text))
        throw new Error('INVALID_MINOR_AMOUNT');
    return BigInt(text).toString();
}
export function majorToMinorExact(value, currency) {
    if (typeof value !== 'string' && (!value || typeof value !== 'object' || typeof value.toString !== 'function' || value.toString === Object.prototype.toString))
        throw new Error('INVALID_MAJOR_AMOUNT');
    const scale = minorDigitsForCurrency(currency), text = value.toString();
    if (text.length > 80 || !/^-?\d+(?:\.\d+)?$/.test(text))
        throw new Error('INVALID_MAJOR_AMOUNT');
    const negative = text.startsWith('-'), [whole, fraction = ''] = (negative ? text.slice(1) : text).split('.');
    if (fraction.slice(scale).replace(/0/g, ''))
        throw new Error('EXCESS_CURRENCY_PRECISION');
    const amount = BigInt(whole) * 10n ** BigInt(scale) + BigInt((fraction.slice(0, scale).padEnd(scale, '0')) || '0');
    return canonicalMinor(negative ? -amount : amount);
}
export function minorToMajorExact(minor, currency) {
    const value = BigInt(canonicalMinor(minor)), scale = minorDigitsForCurrency(currency), abs = value < 0n ? -value : value;
    const digits = abs.toString().padStart(scale + 1, '0');
    return (value < 0n ? '-' : '') + (scale ? digits.slice(0, -scale) + '.' + digits.slice(-scale) : digits);
}
export function toSafeMinorNumber(minor) {
    const value = BigInt(canonicalMinor(minor));
    if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER))
        throw new Error('UNSAFE_NUMBER_AMOUNT');
    return Number(value);
}
export function moneyFromMajor(value, currency) {
    return Object.freeze({ version: 1, currency, minor: majorToMinorExact(value, currency), scale: minorDigitsForCurrency(currency) });
}
export function addExactMoney(a, b) {
    if (a.version !== 1 || b.version !== 1 || a.currency !== b.currency || a.scale !== b.scale || a.scale !== minorDigitsForCurrency(a.currency))
        throw new Error('MONEY_CURRENCY_MISMATCH');
    return Object.freeze({ ...a, minor: canonicalMinor(BigInt(canonicalMinor(a.minor)) + BigInt(canonicalMinor(b.minor))) });
}
/** Explicit provider-number adapter; precision and round-trip must remain exact. */
export function minorToProviderMajor(minor, currency) {
    toSafeMinorNumber(minor);
    const value = Number(minorToMajorExact(minor, currency));
    if (!Number.isFinite(value) || majorToMinorExact(value.toString(), currency) !== canonicalMinor(minor))
        throw new Error('UNSAFE_PROVIDER_AMOUNT');
    return value;
}
/** Presentation uses original currency and exact integer/fraction, including large totals. */
export function formatExactMoney(money, locale = 'az-AZ') {
    if (money.version !== 1 || money.scale !== minorDigitsForCurrency(money.currency))
        throw new Error('INVALID_MONEY_CONTRACT');
    const value = BigInt(canonicalMinor(money.minor)), scale = money.scale, factor = 10n ** BigInt(scale), abs = value < 0n ? -value : value;
    const whole = abs / factor, fraction = (abs % factor).toString().padStart(scale, '0');
    const signed = value < 0n ? (whole === 0n ? -0 : -whole) : whole;
    return new Intl.NumberFormat(locale, { style: 'currency', currency: money.currency, minimumFractionDigits: scale, maximumFractionDigits: scale }).formatToParts(signed)
        .map(part => part.type === 'fraction' ? fraction : part.value).join('');
}
